'use client'

import { cva } from 'class-variance-authority'
import type { InputHTMLAttributes } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { cn } from '~/core/ui/utils'

const inputVariants = cva(
  'flex w-full min-w-full items-center justify-center rounded-sm bg-white px-3 text-gray-900 outline-hidden placeholder:text-gray-600 disabled:pointer-events-none disabled:text-gray-400 disabled:placeholder:text-gray-400 dark:text-gray-200 dark:placeholder:text-gray-400 dark:disabled:text-gray-700 dark:disabled:placeholder:text-gray-700',
  {
    variants: {
      variant: {
        defaultNoDestructive:
          'focus:border-primary-300 focus:shadow-select dark:focus:border-primary-700 dark:focus:shadow-dark-select border border-solid border-gray-300 disabled:border-gray-200 disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:disabled:border-gray-700 dark:disabled:bg-gray-800',
        defaultDestructive:
          'focus:shadow-error dark:focus:shadow-dark-error border border-solid border-red-300 dark:border-red-700 dark:bg-gray-900',
        ghostNoDestructive:
          'focus:border-primary-300 focus:shadow-select dark:focus:border-primary-700 dark:focus:shadow-dark-select hover:bg-gray-50 focus:border focus:border-solid focus:bg-transparent disabled:bg-white dark:bg-gray-900 dark:disabled:bg-gray-800',
        ghostDestructive:
          'focus:shadow-select dark:focus:border-primary-700 dark:focus:shadow-dark-select hover:bg-gray-50 focus:border focus:border-solid focus:border-red-300 focus:bg-transparent disabled:bg-white dark:bg-gray-900 dark:disabled:bg-gray-800'
      },
      size: {
        lg: 'h-[42px] text-base',
        md: 'h-[38px] text-sm',
        sm: 'h-8 text-sm',
        xs: 'h-6 text-xs'
      },
      focus: {
        default: '',
        focus:
          'border-primary-300 shadow-select dark:border-primary-700 dark:shadow-dark-select border border-solid dark:bg-gray-900'
      }
    },
    defaultVariants: {
      variant: 'defaultNoDestructive',
      size: 'md',
      focus: 'default'
    }
  }
)

type InputSizeProps = 'lg' | 'md' | 'sm' | 'xs'
type InputVariantProps = 'default' | 'ghost'
interface InputOptionsProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'size' | 'value' | 'onChange' | 'onBlur'
  > {
  onChange?: (value: string | number) => void
  onBlur?: (value: string | number) => void
  onChangeEnter?: () => void
  inputType?: 'text' | 'number'
  defaultValue?: string | number
  value?: string | number

  isDisabled?: boolean
  destructive?: boolean
  className?: string
  placeholder?: string

  variant?: InputVariantProps
  size?: InputSizeProps
  isFocus?: boolean
  config?: object
  autoFocusFirstLoad?: boolean
}

interface InputProps extends InputOptionsProps {
  ref?: React.Ref<HTMLInputElement>
}

function Input({
  onChange = undefined,
  onBlur = undefined,
  onChangeEnter = undefined,
  inputType = 'text',
  isDisabled = false,
  destructive = false,
  className = '',
  variant = 'default',
  size = 'md',
  isFocus = false,
  autoFocusFirstLoad = false,
  value,
  ref,
  ...props
}: InputProps) {
  const internalRef = useRef<HTMLInputElement>(null)
  const [focusFirstLoad, setFocus] = useState(true)

  const targetRef = autoFocusFirstLoad
    ? internalRef
    : (ref as React.RefObject<HTMLInputElement>)

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      if (onChange) {
        onChange(newValue)
      }
    },
    [onChange]
  )

  const handleBlur = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(e.target.value)
      }
    },
    [onBlur]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && onChangeEnter) {
        onChangeEnter()
      }
    },
    [onChangeEnter]
  )

  useEffect(() => {
    if (focusFirstLoad && autoFocusFirstLoad && internalRef.current) {
      setFocus(false)
      setTimeout(() => {
        internalRef.current?.focus()
      }, 250)
    }
  }, [focusFirstLoad, autoFocusFirstLoad])

  return (
    <input
      ref={targetRef}
      type={inputType}
      disabled={isDisabled}
      className={cn(
        'cursor-pointer',
        props.type === 'number'
          ? '[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
          : '',
        inputVariants({
          variant: `${variant}${destructive ? 'Destructive' : 'NoDestructive'}`,
          size,
          focus: isFocus ? 'focus' : 'default',
          className
        })
      )}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      value={value ?? ''}
      {...props}
    />
  )
}

Input.displayName = 'Input'

export { Input }
export type { InputOptionsProps, InputProps, InputSizeProps, InputVariantProps }
